import {keys} from 'ts-transformer-keys';

import User, {View} from '~/shared/src/model/User';
import objectUtils from '~/shared/src/utilities/objectUtils';
import {
    ColumnConfig,
    ColumnDefinition
} from '~/shared/src/validationSchemes/modules/overview';

import {store} from '~/frontend/src/store';
import Overview, {
    ComponentProps as OverviewComponentProps,
    defaultProps as overviewDefaultProps,
    DefaultProps as OverviewDefaultProps
} from '~/frontend/src/components/base/Overview';
import {PanelProps as ViewSelectorDropdownPanelProps} from '~/frontend/src/components/base/shared/field/ViewSelectorDropdown';

const overViewDefaultPropsKeys = keys<OverviewDefaultProps>() as string[];

export const preInitialize = ( props : OverviewComponentProps & { user : User } ) : OverviewComponentProps => {
    const retVal : OverviewComponentProps & { user? : User } = {
        ...props
    };

    delete retVal.user;

    // set key for columns, filters and sorters if not
    if ( retVal.columns?.length ) {
        for ( let i = retVal.columns.length - 1; i >= 0; i-- ) {
            if ( !retVal.columns[ i ].key ) {
                retVal.columns[ i ].key = retVal.columns[ i ].field;
            }
        }
    }

    if ( retVal.filters?.length ) {
        for ( let i = retVal.filters.length - 1; i >= 0; i-- ) {
            if ( !retVal.filters[ i ].key ) {
                retVal.filters[ i ].key = retVal.filters[ i ].field;
            }
        }
    }

    if ( retVal.sorters?.length ) {
        for ( let i = retVal.sorters.length - 1; i >= 0; i-- ) {
            if ( !retVal.sorters[ i ].key ) {
                retVal.sorters[ i ].key = retVal.sorters[ i ].field;
            }
        }
    }

    return retVal;
}

/**
 * Gets the views from user.
 *
 * @param {string} overviewKey
 * @param {ColumnDefinition[]} columnDefinitions
 *
 * @return {View[]}
 */
export const getViews = ( overviewKey : string, columnDefinitions : ColumnDefinition[] ) : View[] => {
    const user                   = store.getState()?.session?.user,
          retVal                 = user
              ? user.getViewsForModule( [
                  Overview.viewSelectorModule,
                  overviewKey
              ] )
              : [],
          columnDefinitionFields = columnDefinitions.map( cD => cD.field );

    // remove columns, filters and sorters no longer defined
    // set key for columns, filters and sorters if not
    for ( let viewIndex = 0; viewIndex < retVal.length; viewIndex++ ) {
        if ( retVal[ viewIndex ].panelProps.columns?.length ) {
            for ( let i = retVal[ viewIndex ].panelProps.columns.length - 1; i >= 0; i-- ) {
                if ( !columnDefinitionFields.includes( retVal[ viewIndex ].panelProps.columns[ i ].field ) ) {
                    retVal[ viewIndex ].panelProps.columns.splice( i, 1 );
                }
                else if ( !retVal[ viewIndex ].panelProps.columns[ i ].key ) {
                    retVal[ viewIndex ].panelProps.columns[ i ].key = columnDefinitions.find( cD => cD.field === retVal[ viewIndex ].panelProps.columns[ i ].field )?.key ?? retVal[ viewIndex ].panelProps.columns[ i ].field;
                }
            }
        }

        if ( retVal[ viewIndex ].panelProps.filters?.length ) {
            for ( let i = retVal[ viewIndex ].panelProps.filters.length - 1; i >= 0; i-- ) {
                if ( !columnDefinitionFields.includes( retVal[ viewIndex ].panelProps.filters[ i ].field ) ) {
                    retVal[ viewIndex ].panelProps.filters.splice( i, 1 );
                }
                else if ( !retVal[ viewIndex ].panelProps.filters[ i ].key ) {
                    retVal[ viewIndex ].panelProps.filters[ i ].key = columnDefinitions.find( cD => cD.field === retVal[ viewIndex ].panelProps.filters[ i ].field )?.key ?? retVal[ viewIndex ].panelProps.filters[ i ].field;
                }
            }
        }

        if ( retVal[ viewIndex ].panelProps.sorters?.length ) {
            for ( let i = retVal[ viewIndex ].panelProps.sorters.length - 1; i >= 0; i-- ) {
                if ( !columnDefinitionFields.includes( retVal[ viewIndex ].panelProps.sorters[ i ].field ) ) {
                    retVal[ viewIndex ].panelProps.sorters.splice( i, 1 );
                }
                else if ( !retVal[ viewIndex ].panelProps.sorters[ i ].key ) {
                    retVal[ viewIndex ].panelProps.sorters[ i ].key = columnDefinitions.find( cD => cD.field === retVal[ viewIndex ].panelProps.sorters[ i ].field )?.key ?? retVal[ viewIndex ].panelProps.sorters[ i ].field;
                }
            }
        }
    }

    // reorder by label
    retVal.sort( ( val1, val2 ) => {
        if ( val1.label > val2.label ) {
            return 1;
        }
        else if ( val1.label < val2.label ) {
            return -1;
        }
        else {
            return 0;
        }
    } );

    return retVal;
}

/**
 * Gets the default view for given parameters from the given user if there are some.
 *
 * @param {string} overviewKey
 * @param {ColumnDefinition[]} columnDefinitions
 *
 * @return {Partial<OverviewComponentProps>}
 */
export const getDefaultViewProps = ( overviewKey : string, columnDefinitions : ColumnDefinition[] ) : PanelProps => {
    const views = getViews( overviewKey, columnDefinitions );

    let retVal : PanelProps = {};

    for ( const view of views ) {
        if ( view.panelProps.default ) {
            retVal = view.panelProps;
            break;
        }
    }

    return retVal;
}

/**
 * Extracts the minimal viewProps from given props.
 *
 * @param {OverviewComponentProps} props
 * @param {( keyof OverviewComponentProps )[]} excludePropKeys
 *
 * @return {Partial<OverviewComponentProps>}
 */
export const extractViewPropsFromProps = ( props : OverviewComponentProps, excludePropKeys : ( keyof OverviewComponentProps )[] = [] ) : Partial<OverviewComponentProps> => {
    const retVal                 = {} as ( Partial<OverviewComponentProps> & { overviewConfigHash? : string } ),
          propKeys               = Object.keys( props ) as ( keyof OverviewComponentProps )[],
          allowedPropKeys        = overViewDefaultPropsKeys,
          defaultExcludePropKeys = [
              'schema',             // defined by component
              'timezone',           // defined by component
              'dataStructure',      // defined by component
              'columnDefinitions',  // defined by component
              'fixFilters',         // defined by component
              'editable',           // defined by component
              'forceFit',           // will be always false
              'autoLayout',         // will be always false
              'className',          // defined by component
              'doSearch',
              'page',               // paging position is not part of a view
              'scrollLeft',
              'scrollTop',
              'mode',
              'showViewAsConfigured',
              'queryType'
          ] as ( keyof OverviewComponentProps )[];

    // allowed prop (only OverviewDefaultProps are allowed)
    // no default exclude prop
    // no given exclude prop
    // not equals default
    for ( const propKey of propKeys ) {
        if (
            allowedPropKeys.includes( propKey )
            && !defaultExcludePropKeys.includes( propKey )
            && !excludePropKeys.includes( propKey )
            && JSON.stringify( props[ propKey ] ) !== JSON.stringify( overviewDefaultProps[ propKey as keyof OverviewDefaultProps ] )
        ) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            retVal[ propKey ] = props[ propKey ];
        }
    }

    retVal.overviewConfigHash = props.configHash;

    return retVal;
}

/**
 * Build the full set of props to set for given viewProps.
 *
 * @param {Partial<OverviewDefaultProps>} props
 * @param {( keyof OverviewComponentProps )[]} excludePropKeys
 *
 * @return {Partial<OverviewComponentProps>}
 */
export const buildPropsFromViewProps = ( props : Partial<OverviewDefaultProps>, excludePropKeys : ( keyof OverviewComponentProps )[] = [] ) : Partial<OverviewDefaultProps> => {
    props = {
        ...overviewDefaultProps,
        ...props
    };

    const retVal                 = {} as Partial<OverviewDefaultProps>,
          propKeys               = Object.keys( props ) as ( keyof OverviewDefaultProps )[],
          allowedPropKeys        = overViewDefaultPropsKeys,
          defaultExcludePropKeys = [
              'overviewConfigHash', // only for compare, do not set back!
              'schema',             // defined by component
              'timezone',           // defined by component
              'dataStructure',      // defined by component
              'columnDefinitions',  // defined by component
              'fixFilters',         // defined by component
              'editable',           // defined by component
              'className',          // defined by component
              'doSearch',
              'scrollLeft',
              'scrollTop',
              'mode',
              'showViewAsConfigured',
              'queryType'
          ] as ( keyof OverviewComponentProps )[];

    // allowed prop (only ItemFinderDataTableDefaultProps are allowed)
    // no default exclude prop
    // no given exclude prop
    for ( const propKey of propKeys ) {
        if (
            allowedPropKeys.includes( propKey )
            && !defaultExcludePropKeys.includes( propKey )
            && !excludePropKeys.includes( propKey )
        ) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            retVal[ propKey ] = props[ propKey ];
        }
    }

    retVal.doSearch = retVal.search;

    return retVal;
}

/**
 * Compares the given panelProps with the given viewPanelProps.
 *
 * @param {ViewSelectorDropdownPanelProps} panelProps
 * @param {ViewSelectorDropdownPanelProps} viewPanelProps
 *
 * @return {boolean}
 *
 * @private
 */
export const viewSelectorPanelPropsCompare = ( panelProps : ViewSelectorDropdownPanelProps, viewPanelProps : ViewSelectorDropdownPanelProps ) : boolean => {
    const forceFit   = viewPanelProps.forceFit || false,
          autoLayout = viewPanelProps.autoLayout || false;

    panelProps = extractViewPropsFromProps( panelProps as OverviewComponentProps, [] );
    viewPanelProps = extractViewPropsFromProps( viewPanelProps as OverviewComponentProps, [] );

    delete panelProps.overviewConfigHash;
    delete viewPanelProps.overviewConfigHash;

    delete panelProps.editMode;
    delete viewPanelProps.editMode;

    delete panelProps.search;
    delete viewPanelProps.search;

    if ( !panelProps.rowStyling?.length ) {
        delete panelProps.rowStyling;
    }
    if ( !viewPanelProps.rowStyling?.length ) {
        delete viewPanelProps.rowStyling;
    }

    // set all defaultProps set in panelProps to viewPanelProps with their defaults
    for ( const key of overViewDefaultPropsKeys ) {
        if ( panelProps[ key ] !== undefined && viewPanelProps[ key ] === undefined ) {
            viewPanelProps[ key ] = overviewDefaultProps[ key as keyof OverviewDefaultProps ];
        }
    }

    // remove column width if forceFit or autoLayout
    if ( panelProps.columns && ( forceFit || autoLayout ) ) {
        panelProps.columns = [].concat( panelProps.columns );

        for ( let i = 0; i < panelProps.columns.length; i++ ) {
            panelProps.columns[ i ] = Object.assign( {}, panelProps.columns[ i ] ) as ColumnConfig;
            delete panelProps.columns[ i ].width;
        }
    }

    // remove column width if forceFit or autoLayout
    if ( viewPanelProps.columns && ( forceFit || autoLayout ) ) {
        viewPanelProps.columns = [].concat( viewPanelProps.columns );

        for ( let i = 0; i < viewPanelProps.columns.length; i++ ) {
            viewPanelProps.columns[ i ] = Object.assign( {}, viewPanelProps.columns[ i ] ) as ColumnConfig;
            delete viewPanelProps.columns[ i ].width;
        }
    }

    return !objectUtils.getDiffKeys( panelProps, viewPanelProps ).length;
}
